import React, { useState, useRef, useEffect } from 'react'
import { User, LogOut, FolderOpen, LogIn, UserPlus, ChevronDown, Compass } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useLocation, useNavigate } from 'react-router-dom'
import { useModelStore } from '@stores/modelStore'
import { useAuthModal } from '@/contexts/AuthModalContext'

export const UserMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { openLogin, openSignup } = useAuthModal()

  // Get model state for auto-save before navigation (only for My Diagrams)
  const model = useModelStore((state) => ({
    version: state.version,
    title: state.title,
    description: state.description,
    colors: state.colors,
    icons: state.icons,
    items: state.items,
    views: state.views
  }))

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Auto-save current work to localStorage before navigation
  const autoSaveBeforeNavigate = () => {
    try {
      // Only save if there's content
      if (model.items && model.items.length > 0) {
        const modelJSON = JSON.stringify(model)
        localStorage.setItem('flowi-autosave', modelJSON)
        localStorage.setItem('flowi-autosave-timestamp', new Date().toISOString())
        const diagramId = location.pathname.match(/^\/diagram\/([^/]+)$/)?.[1]
        if (diagramId) {
          localStorage.setItem('flowi-autosave-diagram-id', diagramId)
        } else {
          localStorage.removeItem('flowi-autosave-diagram-id')
        }
        console.log('✅ Auto-saved before navigation')
      }
    } catch (error) {
      console.error('Failed to auto-save before navigation:', error)
    }
  }

  const handleSignOut = async () => {
    autoSaveBeforeNavigate()
    await signOut()
    setIsOpen(false)
    navigate('/')
  }

  const handleMyDiagrams = () => {
    autoSaveBeforeNavigate()
    setIsOpen(false)
    navigate('/my-diagrams')
  }

  const handleExplore = () => {
    autoSaveBeforeNavigate()
    setIsOpen(false)
    navigate('/explore')
  }

  const handleLogin = () => {
    setIsOpen(false)
    openLogin()
  }

  const handleSignup = () => {
    setIsOpen(false)
    openSignup()
  }

  const getUserInitials = () => {
    if (!user?.email) return 'U'
    return user.email.charAt(0).toUpperCase()
  }

  return (
    <div className="relative" ref={menuRef}>
      {user ? (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-semibold shadow-sm">
            {getUserInitials()}
          </div>
          <span className="hidden md:inline text-sm font-medium text-gray-700 dark:text-gray-300 max-w-[100px] truncate">
            {user.email?.split('@')[0] || 'User'}
          </span>
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <User className="h-4 w-4" />
          <span className="hidden md:inline">Account</span>
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      )}

      {isOpen && (
        <div className="absolute right-0 top-full mt-1.5 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
          {user ? (
            <>
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user.email}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Personal account
                </p>
              </div>

              <div className="py-1.5">
                <button
                  onClick={handleMyDiagrams}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <FolderOpen className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>My Diagrams</span>
                </button>

                <button
                  onClick={handleExplore}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <Compass className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>Explore Public Diagrams</span>
                </button>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700" />

              <div className="py-1.5">
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <LogOut className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>Sign out</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Welcome to Degre120
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Sign in to save your work
                </p>
              </div>

              <div className="py-1.5">
                <button
                  onClick={handleExplore}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <Compass className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>Explore Public Diagrams</span>
                </button>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700" />

              <div className="py-1.5">
                <button
                  onClick={handleLogin}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <LogIn className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>Sign in</span>
                </button>

                <button
                  onClick={handleSignup}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <UserPlus className="h-4 w-4" />
                  <span>Create account</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
